import { useCallback } from "react";
import { toast } from "@jsq/ds/core";
import {
  useApproveIngestionClassificationMutation,
  useReclassifyDocumentMutation,
  useRemoveDocumentFromIngestionMutation,
} from "#graphql/supergraphHooks";
import { useRequiredArenaId } from "./useRequiredArenaId";

// Write-path seam for the classification confirm screen.
//
// reclassifyDocument, removeDocumentFromIngestion, and the terminal
// approveIngestionClassification are all composed into the supergraph, so every
// hook calls its generated mutation. The return interfaces are the contract the
// UI binds to, so the components stay untouched.
//
// Each takes the arena id as an argument: writes fire on user action, well after
// the host app's arena resolves, so an absent id is a programming error that
// throws rather than silently no-ops.

/** Mirrors fay's `ReclassifyDocumentInput`. `entityId` is `Int` on the wire; we
 *  carry it as the raw string the UI holds and coerce to an integer here. */
export type ReclassifyDocumentInput = {
  documentId: string;
  documentType?: string | null;
  reportingDate?: string | null;
  entityId?: string | null;
};

export type UseReclassifyDocumentReturn = {
  reclassify: (input: ReclassifyDocumentInput) => Promise<void>;
  loading: boolean;
};

// The wire `entityId` is `Int`. The picker decodes relay global ids to numeric
// strings (e.g. "99"), which pass through. Anything that isn't a plain integer
// coerces to null so a malformed id never reaches the mutation.
function toEntityIdInt(value?: string | null): number | null {
  if (value == null || value === "") {
    return null;
  }
  const parsed = Number(value);
  return Number.isInteger(parsed) ? parsed : null;
}

/** Per-document edit (type / entity / reporting date). */
export function useReclassifyDocument(
  arenaId: number | undefined,
): UseReclassifyDocumentReturn {
  const [mutate, { loading }] = useReclassifyDocumentMutation();
  const getArenaId = useRequiredArenaId(arenaId);
  const reclassify = useCallback(
    async (input: ReclassifyDocumentInput) => {
      // Resolve the arena before the try so a missing-arena programming error
      // surfaces loudly instead of being reported as a mutation failure.
      const resolvedArenaId = getArenaId();
      try {
        await mutate({
          variables: {
            input: {
              arenaId: resolvedArenaId,
              documentId: input.documentId,
              documentType: input.documentType ?? null,
              reportingDate: input.reportingDate ?? null,
              entityId: toEntityIdInt(input.entityId),
            },
          },
        });
      } catch (err) {
        toast.error("Couldn’t update the document. Please try again.");
        // Re-throw so the caller can roll back its optimistic overlay on failure.
        throw err;
      }
    },
    [mutate, getArenaId],
  );
  return { reclassify, loading };
}

export type UseRemoveDocumentReturn = {
  remove: (documentId: string) => Promise<void>;
  loading: boolean;
};

/** Per-document remove (soft-delete; backend honors never-delete). */
export function useRemoveDocumentFromIngestion(
  arenaId: number | undefined,
): UseRemoveDocumentReturn {
  const [mutate, { loading }] = useRemoveDocumentFromIngestionMutation();
  const getArenaId = useRequiredArenaId(arenaId);
  const remove = useCallback(
    async (documentId: string) => {
      // Resolve the arena before the try so a missing-arena programming error
      // surfaces loudly instead of being reported as a mutation failure.
      const resolvedArenaId = getArenaId();
      try {
        await mutate({ variables: { arenaId: resolvedArenaId, documentId } });
      } catch (err) {
        toast.error("Couldn’t remove the document. Please try again.");
        // Re-throw so the caller keeps the doc in the batch on failure.
        throw err;
      }
    },
    [mutate, getArenaId],
  );
  return { remove, loading };
}

export type ConfirmResult = { ok: boolean };

export type UseConfirmClassificationReturn = {
  confirm: (ingestionId: string, documentIds: string[]) => Promise<ConfirmResult>;
  loading: boolean;
};

/**
 * Batch approve ("Approve classification of N documents"). Calls fay's terminal
 * `approveIngestionClassification`, which binds the proposed classification into
 * deliverable packages. The mutation is keyed by ingestion id; `documentIds` is
 * kept on the signature for the caller's success messaging. Returns `{ ok }` so
 * the UI only depends on `{ confirm, loading }`.
 */
export function useConfirmClassification(
  arenaId: number | undefined,
): UseConfirmClassificationReturn {
  const [mutate, { loading }] = useApproveIngestionClassificationMutation();
  const getArenaId = useRequiredArenaId(arenaId);
  const confirm = useCallback(
    async (ingestionId: string, _documentIds: string[]) => {
      // Resolve the arena before the try so a missing-arena programming error
      // surfaces loudly instead of being swallowed as `{ ok: false }`.
      const resolvedArenaId = getArenaId();
      try {
        await mutate({ variables: { arenaId: resolvedArenaId, ingestionId } });
        return { ok: true };
      } catch {
        toast.error("Couldn’t approve the classification. Please try again.");
        return { ok: false };
      }
    },
    [mutate, getArenaId],
  );
  return { confirm, loading };
}
